/**
 * DOM-modal replacement for the canvas-drawn `drawOnlineOverlay` from
 * `@bn-games/render-helpers`. Same state machine (the 7 OnlineOverlayState
 * cases) and same button IDs (leave / rematch / return-to-menu / cancel /
 * accept / decline), but rendered as a real focus-trapped dialog so the
 * buttons are keyboard- and screen-reader-reachable by default.
 *
 * Consumed by backgammon + checkers today; domino has its own hand/match-end
 * result modal driven by `openModal` directly. Labels stay per-app (each
 * consuming game owns its i18n dictionary), so the helper takes a string
 * config callback rather than hardcoding strings.
 */
import { openModal, type ModalHandle } from "./components/Modal";
import { createAvatarBodyBuilder } from "./gameStateOverlayAvatar";
import { buildGameOverOpponentControl } from "./gameStateOverlayOpponent";

export type GameStateOverlayState =
  | "opponent_disconnected"
  | "reconnecting_self"
  | "game_disconnected"
  | "rematch_offered"
  | "rematch_requested"
  | "rematch_declined"
  | "game_over";

export type GameStateOverlayButtonId =
  | "leave"
  | "rematch"
  | "return-to-menu"
  | "cancel"
  | "accept"
  | "decline";

export interface GameStateOverlaySnapshot {
  readonly state: GameStateOverlayState;
  readonly opponentName: string;
  readonly opponentAvatarUrl?: string | null;
  readonly gracePeriodEndsAt: number | null;
  readonly gameOverResult?: "win" | "loss" | "draw";
  /**
   * `toSeatedPlayers` (`@bn-games/room-server`) only ever sets `userId` /
   * `avatarUrl` / `verified` together, as a trio — so presence of `userId`
   * already means "this opponent is addable"; no separate `verified` flag.
   */
  readonly opponentUserId?: string | null;
  /**
   * `game_over` only — an app-supplied node shown under the result text (e.g.
   * chess's PGN/FEN export buttons). Pass the same node on every update: the
   * body is rebuilt only when the node itself changes.
   */
  readonly extraContent?: HTMLElement | null;
}

export interface GameStateOverlayText {
  readonly title: string;
  readonly body?: string;
}

/**
 * Builds the localized strings for a given snapshot. Caller resolves i18n,
 * countdown formatting, and any game-specific phrasing (e.g. the rematch
 * preview line that includes the upcoming color). Mirrors the shape of
 * `OverlayLabels` from the canvas widget so migrating call-sites is a
 * straight swap.
 */
export interface GameStateOverlayLabels {
  readonly opponentDisconnected: (opponentName: string, countdown: string) => GameStateOverlayText;
  readonly reconnectingSelf: (countdown: string) => GameStateOverlayText;
  readonly gameDisconnected: () => GameStateOverlayText;
  readonly rematchOffered: (opponentName: string) => GameStateOverlayText;
  readonly rematchRequested: (opponentName: string) => GameStateOverlayText;
  readonly rematchDeclined: (opponentName: string) => GameStateOverlayText;
  readonly gameOver: (result: "win" | "loss" | "draw") => GameStateOverlayText;
  readonly buttons: Readonly<Record<GameStateOverlayButtonId, string>>;
}

export interface GameStateOverlayHandlers {
  readonly onAction: (id: GameStateOverlayButtonId) => void;
}

export interface GameStateOverlayHandle {
  /** Apply the latest snapshot. Opens the modal on first call. */
  readonly update: (snapshot: GameStateOverlaySnapshot) => void;
  /** Close + tear down. Safe to call multiple times. */
  readonly close: () => void;
  /** True while the underlying modal is mounted. */
  readonly isOpen: () => boolean;
}

interface RenderSpec {
  readonly title: string;
  readonly body?: string;
  readonly avatarUrl?: string | null;
  /** `game_over` only — the addressee for `applyDelta`'s rebuild guard. */
  readonly opponentUserId?: string | null;
  /** `game_over` only — built by `createAddOpponentControl` in `specFor`. */
  readonly addOpponentControl?: HTMLElement | null;
  /** `game_over` only — the caller's own node, appended last. */
  readonly extraContent?: HTMLElement | null;
  readonly buttons: readonly GameStateOverlayButtonId[];
  /** The button id that should be the primary (highlighted, Enter-default). */
  readonly primary?: GameStateOverlayButtonId;
}

function specFor(
  snapshot: GameStateOverlaySnapshot,
  labels: GameStateOverlayLabels,
): RenderSpec {
  const countdown = formatCountdown(snapshot.gracePeriodEndsAt);
  switch (snapshot.state) {
    case "opponent_disconnected": {
      const t = labels.opponentDisconnected(snapshot.opponentName, countdown);
      return {
        title: t.title,
        body: t.body,
        avatarUrl: snapshot.opponentAvatarUrl,
        buttons: ["leave"],
        primary: "leave",
      };
    }
    case "reconnecting_self": {
      const t = labels.reconnectingSelf(countdown);
      return { title: t.title, body: t.body, buttons: ["leave"], primary: "leave" };
    }
    case "game_disconnected": {
      const t = labels.gameDisconnected();
      return { title: t.title, body: t.body, buttons: ["return-to-menu"], primary: "return-to-menu" };
    }
    case "rematch_offered": {
      // Local seat already proposed; we're waiting on the opponent.
      const t = labels.rematchOffered(snapshot.opponentName);
      return {
        title: t.title,
        body: t.body,
        avatarUrl: snapshot.opponentAvatarUrl,
        buttons: ["cancel"],
        primary: "cancel",
      };
    }
    case "rematch_requested": {
      // Opponent proposed; we accept or decline.
      const t = labels.rematchRequested(snapshot.opponentName);
      return {
        title: t.title,
        body: t.body,
        avatarUrl: snapshot.opponentAvatarUrl,
        buttons: ["accept", "decline"],
        primary: "accept",
      };
    }
    case "rematch_declined": {
      const t = labels.rematchDeclined(snapshot.opponentName);
      return {
        title: t.title,
        body: t.body,
        avatarUrl: snapshot.opponentAvatarUrl,
        buttons: ["leave"],
        primary: "leave",
      };
    }
    case "game_over": {
      const result = snapshot.gameOverResult ?? "draw";
      const t = labels.gameOver(result);
      const { opponentUserId, addOpponentControl } = buildGameOverOpponentControl(snapshot);
      return {
        title: t.title,
        body: t.body,
        opponentUserId,
        addOpponentControl,
        extraContent: snapshot.extraContent ?? null,
        buttons: ["rematch", "return-to-menu"],
        primary: "rematch",
      };
    }
  }
}

function formatCountdown(endsAt: number | null): string {
  if (endsAt === null) return "";
  const remaining = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
  return `${remaining}s`;
}

/**
 * Open a fresh game-state overlay. The handle is stateful — the caller
 * `update()`s it as the snapshot changes (countdown ticks, rematch flow
 * transitions, etc.) without re-creating the DOM node, which keeps focus
 * stable inside the trap.
 *
 * The modal is non-dismissible (no click-outside / Escape close) — these are
 * authoritative states the user has to actively resolve via a button.
 */
export function createGameStateOverlay(
  /**
   * Labels can be passed directly (static — fine for tests + simple cases) or
   * as a getter (per-app — resolves the current locale / upcoming-color
   * context on each update without forcing the consumer to rebuild the
   * overlay handle on language or rematch transitions).
   */
  labels: GameStateOverlayLabels | (() => GameStateOverlayLabels),
  handlers: GameStateOverlayHandlers,
): GameStateOverlayHandle {
  const resolveLabels = (): GameStateOverlayLabels =>
    typeof labels === "function" ? labels() : labels;

  let modal: ModalHandle | null = null;
  let currentSpec: RenderSpec | null = null;
  const avatarBody = createAvatarBodyBuilder();

  function buildActions(spec: RenderSpec, currentLabels: GameStateOverlayLabels) {
    return spec.buttons.map((id) => ({
      label: currentLabels.buttons[id],
      primary: id === spec.primary,
      onClick: () => handlers.onAction(id),
    }));
  }

  function open(spec: RenderSpec, currentLabels: GameStateOverlayLabels): void {
    modal = openModal({
      title: spec.title,
      body: avatarBody.build(spec),
      actions: buildActions(spec, currentLabels),
      dismissible: false,
    });
    currentSpec = spec;
    // Pull focus into the modal so SR + keyboard users land on the primary
    // action instead of remaining on whatever canvas/menu element fired the
    // state change. Microtask defers past the openModal `document.body.append`
    // tick so the button is mounted before we `.focus()` it.
    const handle = modal;
    queueMicrotask(() => {
      if (modal === handle) handle.focusPrimary();
    });
  }

  function applyDelta(spec: RenderSpec, currentLabels: GameStateOverlayLabels): void {
    if (modal === null) return;
    if (currentSpec === null || currentSpec.title !== spec.title) {
      modal.setTitle(spec.title);
    }
    if (
      currentSpec === null ||
      currentSpec.body !== spec.body ||
      currentSpec.avatarUrl !== spec.avatarUrl ||
      currentSpec.opponentUserId !== spec.opponentUserId ||
      (currentSpec.extraContent ?? null) !== (spec.extraContent ?? null)
    ) {
      modal.setBody(avatarBody.build(spec));
    }
    // Only rebuild the action row when the button set OR the primary marker
    // changes — re-creating CrestButton DOM nodes every countdown tick would
    // steal focus from whichever button currently has it, but a stale primary
    // highlight when a future state reuses the same IDs would be worse.
    const buttonsChanged = !currentSpec ||
      currentSpec.buttons.length !== spec.buttons.length ||
      currentSpec.buttons.some((id, i) => id !== spec.buttons[i]) ||
      currentSpec.primary !== spec.primary;
    if (buttonsChanged) {
      modal.setActions(buildActions(spec, currentLabels));
    }
    currentSpec = spec;
  }

  return {
    update: (snapshot) => {
      const currentLabels = resolveLabels();
      const spec = specFor(snapshot, currentLabels);
      if (modal === null) {
        open(spec, currentLabels);
      } else {
        applyDelta(spec, currentLabels);
      }
    },
    close: () => {
      modal?.close();
      modal = null;
      currentSpec = null;
    },
    isOpen: () => modal !== null,
  };
}
